import React from "react";
import ChevronRightIcon from "@src/lib/icons/12px/chevron-right";
import Text from "@src/lib/ui/Text";
import { cn } from "@src/lib/utils/cn";

// The one card shape in the agent thread (sources, files, plans): a gray
// rounded-2xl surface, a fanned stack of framed tiles, a title over a muted
// line, and a white round action on the right.
export const OPUS_AGENT_CARD_CLASS =
  "group flex w-full min-w-0 items-center gap-3 rounded-2xl bg-bg-secondary p-3 text-left transition-colors duration-150 motion-reduce:transition-none";
export const OPUS_AGENT_CARD_INTERACTIVE_CLASS =
  "hover:bg-bg-tertiary focus-visible:ring-1 focus-visible:ring-brand-border focus-visible:outline-none";

// Front tile first. Tiles behind it fan out to the right and tilt the other
// way; on hover the fan opens a little.
const LAYER_CLASSES = [
  "z-30 -rotate-6 group-hover:-rotate-8",
  "z-20 translate-x-2 rotate-3 group-hover:translate-x-2.5 group-hover:rotate-6",
  "z-10 translate-x-4 rotate-8 group-hover:translate-x-5 group-hover:rotate-12",
];
const STACK_WIDTH = ["w-11", "w-13", "w-15"];

export const OpusAgentCardMedia: React.FC<{
  children: React.ReactNode;
  // How many tiles to show, front included (1–3); the ones behind are blank.
  count?: number;
  className?: string;
}> = ({ children, count = 1, className }) => {
  const layers = Math.min(Math.max(count, 1), LAYER_CLASSES.length);
  return (
    <span
      aria-hidden
      className={cn("relative h-11 shrink-0", STACK_WIDTH[layers - 1])}
    >
      {Array.from({ length: layers }, (_, index) => (
        <span
          key={index}
          className={cn(
            // The frame is 2px of white around the tile: 12 − 2 → rounded-lg.
            "absolute top-0 left-0 size-11 rounded-xl bg-bg-primary p-0.5 shadow-large transition-transform duration-200 ease-out motion-reduce:transition-none",
            layers === 1
              ? "z-30 -rotate-6 group-hover:-rotate-3"
              : LAYER_CLASSES[index],
          )}
        >
          <span
            className={cn(
              "flex size-full items-center justify-center overflow-hidden rounded-lg",
              index === 0 ? className : "bg-bg-tertiary",
            )}
          >
            {index === 0 ? children : null}
          </span>
        </span>
      ))}
    </span>
  );
};

export const OpusAgentCardText: React.FC<{
  title: React.ReactNode;
  subtitle: React.ReactNode;
  titleTestId?: string;
  subtitleTestId?: string;
  subtitleId?: string;
  // Beside the muted line, e.g. a status pill.
  badge?: React.ReactNode;
}> = ({ title, subtitle, titleTestId, subtitleTestId, subtitleId, badge }) => (
  <span className="flex min-w-0 flex-1 flex-col">
    <Text
      variant="compact-title"
      ellipsis
      className="text-text-primary"
      data-testid={titleTestId}
    >
      {title}
    </Text>
    <span id={subtitleId} className="flex min-w-0 items-center gap-1.5">
      {/* truncate, not ellipsis: the line sizes to its text so a badge can
          sit right after it. */}
      <Text
        variant="fine-print-description"
        className="min-w-0 truncate text-text-tertiary"
        data-testid={subtitleTestId}
      >
        {subtitle}
      </Text>
      {badge}
    </span>
  </span>
);

// Drawn, not a button: the whole card is the control, so a nested button
// would be invalid inside its link or button.
export const OpusAgentCardAction: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => (
  <span
    aria-hidden
    className="flex size-6 shrink-0 items-center justify-center rounded-full bg-bg-primary text-icon-secondary shadow-default transition-colors group-hover:text-icon-primary [&_svg:not([class*='size-'])]:size-3"
  >
    {children ?? <ChevronRightIcon />}
  </span>
);
